import { librarySectionApi } from '../api/librarySectionApi';
import {
    CreateLibrarySectionRequest,
    DynamicRulesResponse,
    LibrarySection,
    LibrarySectionType,
    SectionOrderItem,
    UpdateLibrarySectionRequest,
} from '../types/library';
import {
    useBaseMutation,
    useBaseQuery,
    useInvalidateQueries,
    useNoDataMutation,
} from './useBaseQuery';
import { libraryKeys } from './useLibrary';

export const librarySectionKeys = {
    all: ['librarySection'] as const,
    lists: () => [...librarySectionKeys.all, 'list'] as const,
    list: (type?: LibrarySectionType) =>
        [...librarySectionKeys.lists(), { type: type ?? null }] as const,
    dynamicRules: () => [...librarySectionKeys.all, 'dynamicRules'] as const,
};

export const useLibrarySections = (type?: LibrarySectionType) => {
    return useBaseQuery<LibrarySection[]>(
        librarySectionKeys.list(type),
        () => librarySectionApi.getAll(type),
        {
            staleTime: 5 * 60 * 1000,
        }
    );
};

export const useDynamicRules = () => {
    return useBaseQuery<DynamicRulesResponse>(
        librarySectionKeys.dynamicRules(),
        () => librarySectionApi.getDynamicRules(),
        {
            staleTime: 30 * 60 * 1000,
        }
    );
};

export const useCreateLibrarySection = () => {
    const { invalidate, invalidateMultiple } = useInvalidateQueries();

    return useBaseMutation<CreateLibrarySectionRequest, LibrarySection>(
        (req) => librarySectionApi.create(req),
        {
            onSuccess: () => {
                invalidate(librarySectionKeys.lists());
                invalidateMultiple([libraryKeys.all]);
            },
        }
    );
};

export const useUpdateLibrarySection = () => {
    const { invalidate, invalidateMultiple } = useInvalidateQueries();

    return useBaseMutation<
        { id: number; data: UpdateLibrarySectionRequest },
        LibrarySection
    >(({ id, data }) => librarySectionApi.update(id, data), {
        onSuccess: () => {
            invalidate(librarySectionKeys.lists());
            invalidateMultiple([libraryKeys.all]);
        },
    });
};

export const useDeleteLibrarySection = () => {
    const { invalidate, invalidateMultiple } = useInvalidateQueries();

    return useNoDataMutation<number>((id) => librarySectionApi.delete(id), {
        onSuccess: () => {
            invalidate(librarySectionKeys.lists());
            invalidateMultiple([libraryKeys.all]);
        },
    });
};

export const useReorderLibrarySections = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<SectionOrderItem[]>(
        (items) => librarySectionApi.reorder(items),
        {
            onSuccess: () => {
                invalidate(librarySectionKeys.lists());
            },
        }
    );
};

export const useNormalizeLibrarySections = () => {
    const { invalidate, invalidateMultiple } = useInvalidateQueries();

    return useNoDataMutation<void>(
        () => librarySectionApi.normalize(),
        {
            onSuccess: () => {
                invalidate(librarySectionKeys.lists());
                invalidateMultiple([libraryKeys.all]);
            },
        }
    );
};
